import { gql } from '@/shared/graphql/generated/explorer';

export const getDelegatingSince = gql(/* GraphQL */ `
  query GetDelegatingSince($ids: [String!]!) {
    accounts: allAccounts(filter: { idSs58: { in: $ids } }) {
      nodes {
        idSs58
        activity: delegationActivitiesByDelegatorAccountId(condition: { type: DELEGATE }) {
          aggregates {
            max {
              timestamp
            }
          }
        }
      }
    }
  }
`);

export const getDelegationRewards = gql(/* GraphQL */ `
  query GetDelegationRewards($ids: [String!]!) {
    accounts: allAccounts(filter: { idSs58: { in: $ids } }) {
      nodes {
        idSs58
        rewards: latestDelegationRewardsByDelegatorAccountId {
          nodes {
            amount
            valueUsd
          }
        }
      }
    }
  }
`);

export const getDelegationPositionBoundaries = gql(/* GraphQL */ `
  query GetDelegationPositionBoundaries($idSs58: String!) {
    boundaries: allDelegationActivities(
      filter: {
        accountByDelegatorAccountId: { idSs58: { equalTo: $idSs58 } }
        type: { in: [DELEGATE, UNDELEGATE] }
      }
      orderBy: EVENT_ID_ASC
    ) {
      nodes {
        type
        timestamp
      }
    }
  }
`);

export const getDelegationPositionRewards = gql(/* GraphQL */ `
  query GetDelegationPositionRewards($idSs58: String!, $since: Datetime!) {
    rewards: allDelegationActivities(
      filter: {
        accountByDelegatorAccountId: { idSs58: { equalTo: $idSs58 } }
        type: { equalTo: REWARD }
        timestamp: { greaterThanOrEqualTo: $since }
      }
    ) {
      aggregates {
        sum {
          amount
          valueUsd
        }
      }
    }
  }
`);

export const getOperatorApys = gql(/* GraphQL */ `
  query OperatorApys {
    allEpoches(orderBy: ID_DESC, offset: 1, first: 10) {
      nodes {
        id
        apys: operatorEpochDelegationApiesByEpochId {
          nodes {
            apyBp
            operator: operatorByOperatorId {
              account: accountByAccountId {
                idSs58
              }
            }
          }
        }
      }
    }
  }
`);

export const getOperatorDelegationActivitiesQuery = gql(/* GraphQL */ `
  query GetOperatorDelegationActivities($idSs58: String!, $first: Int, $offset: Int) {
    allDelegationActivities(
      filter: { operatorByOperatorId: { accountByAccountId: { idSs58: { equalTo: $idSs58 } } } }
      first: $first
      offset: $offset
      orderBy: EVENT_ID_DESC
    ) {
      nodes {
        id
        type
        amount
        valueUsd
        timestamp
      }
      totalCount
    }
  }
`);

export const getOperatorRewardsHistoryQuery = gql(/* GraphQL */ `
  query GetOperatorRewardsHistory($idSs58: String!, $first: Int, $offset: Int) {
    allOperatorEpoches(
      filter: { operatorByOperatorId: { accountByAccountId: { idSs58: { equalTo: $idSs58 } } } }
      first: $first
      offset: $offset
      orderBy: EPOCH_ID_DESC
    ) {
      nodes {
        id
        operatorRewardAmount
        operatorRewardValueUsd
        epoch: epochByEpochId {
          id
          endBlock: blockByEndBlockId {
            timestamp
          }
        }
      }
      totalCount
    }
  }
`);

export const getValidatorRewardsHistoryQuery = gql(/* GraphQL */ `
  query GetValidatorRewardsHistory($idSs58: String!, $first: Int, $offset: Int) {
    allAuthorityMemberships(
      filter: { validatorByValidatorId: { accountByAccountId: { idSs58: { equalTo: $idSs58 } } } }
      first: $first
      offset: $offset
      orderBy: EPOCH_ID_DESC
    ) {
      nodes {
        id
        reward
        rewardValueUsd
        epoch: epochByEpochId {
          id
          endBlock: blockByEndBlockId {
            timestamp
          }
        }
      }
      totalCount
    }
  }
`);

export const getHistoricalValidators = gql(/* GraphQL */ `
  query HistoricalValidators($idSs58: String!) {
    links: allOperatorValidatorLinks(
      filter: { operatorByOperatorId: { accountByAccountId: { idSs58: { equalTo: $idSs58 } } } }
    ) {
      nodes {
        validator: validatorByValidatorId {
          account: accountByAccountId {
            alias
            idSs58
            role
          }
        }
      }
    }
  }
`);

// the most recent hourly row per account; a null $accounts reads every account in the role
export const getParticipantLatestApys = gql(/* GraphQL */ `
  query ParticipantLatestApys($role: NetworkParticipantRole!, $accounts: [String!], $first: Int!) {
    allNetworkParticipantEarningsHourlies(
      filter: { role: { equalTo: $role }, accountByAccountId: { idSs58: { in: $accounts } } }
      orderBy: BLOCK_DESC
      first: $first
    ) {
      nodes {
        accountId
        projectedApy6H
        projectedApy24H
        projectedApy72H
        projectedApy168H
        projectedApy720H
        account: accountByAccountId {
          idSs58
        }
      }
    }
  }
`);

export const getParticipantApyRatios = gql(/* GraphQL */ `
  query ParticipantApyRatios($role: NetworkParticipantRole!, $accounts: [Int!]!, $first: Int!) {
    allNetworkParticipantEarningsHourlies(
      filter: { role: { equalTo: $role }, accountId: { in: $accounts } }
      orderBy: BLOCK_DESC
      first: $first
    ) {
      nodes {
        accountId
        block
        ratio
      }
    }
  }
`);

export const getParticipantDailyApys = gql(/* GraphQL */ `
  query ParticipantDailyApys(
    $role: NetworkParticipantRole!
    $startDate: Date!
    $endDate: Date!
    $minActiveHours: Int!
  ) {
    allNetworkParticipantEarningsDailies(
      filter: {
        role: { equalTo: $role }
        activeHours: { greaterThanOrEqualTo: $minActiveHours }
        day: { greaterThanOrEqualTo: $startDate, lessThan: $endDate }
      }
    ) {
      groupedAggregates(groupBy: [DAY]) {
        keys
        max {
          projectedApy6H
          projectedApy24H
          projectedApy72H
          projectedApy168H
          projectedApy720H
        }
      }
    }
  }
`);

export const getParticipantTodayApy = gql(/* GraphQL */ `
  query ParticipantTodayApy($role: NetworkParticipantRole!, $since: Datetime!) {
    allNetworkParticipantEarningsHourlies(
      filter: { role: { equalTo: $role }, timestamp: { greaterThanOrEqualTo: $since } }
    ) {
      aggregates {
        max {
          projectedApy6H
          projectedApy24H
          projectedApy72H
          projectedApy168H
          projectedApy720H
        }
      }
    }
  }
`);
